import React from "react";

import { ApexChart } from "@/components/ApexChart";
import type {
  SeriesMetricPoint,
  SeriesPoint,
  TelemetryChartMetric,
} from "@/lib/telemetry";
import { formatSeconds } from "@/components/telemetry/format";

/** Every metric the charts offer, in the order support reads them. */
export const CHART_METRICS: TelemetryChartMetric[] = [
  "current",
  "voltage",
  "power",
  "session_energy",
  "temperature",
  "frequency",
];

export const METRIC_LABEL: Record<TelemetryChartMetric, string> = {
  current: "Current (A)",
  voltage: "Voltage (V)",
  power: "Power (kW)",
  session_energy: "Session energy (kWh)",
  temperature: "Temperature (°C)",
  frequency: "Frequency (Hz)",
};

const METRIC_COLOR: Record<TelemetryChartMetric, string> = {
  current: "#2e9e3a",
  voltage: "#0ea5e9",
  power: "#e11d48",
  session_energy: "#22a699",
  temperature: "#f79009",
  frequency: "#8b5cf6",
};

/** Watt-hours as archived → kilowatt-hours as read. */
const WH_PER_KWH = 1000;

function num(value: number | null | undefined): number | null {
  return typeof value === "number" && isFinite(value) ? value : null;
}

/**
 * The one number a bucket stands for.
 *
 * The backend still downsamples to avg/min/max, but a support engineer reading
 * this page wants the reading, not a statistics vocabulary — so exactly one
 * number per bucket is chosen here, and the words avg/min/max appear nowhere on
 * screen.
 *
 * Which number depends on what the metric *is*:
 *
 * * An instantaneous quantity (current, voltage, temperature, frequency) is
 *   represented by the bucket's mean — the value the charger sat at.
 * * `session_energy` is a cumulative counter, not a level. Its value at the end
 *   of a bucket is the highest reading in that bucket, so the mean would draw a
 *   curve the charger never reported. A session boundary inside a bucket makes
 *   the next bucket drop back to the new session's own total, which is the real
 *   reading and is meant to be visible.
 *
 * `null` means "this bucket measured nothing", and it must stay null: a zero
 * here is a dropout the charger never had.
 */
export function metricValue(
  point: SeriesPoint,
  metric: TelemetryChartMetric,
): number | null {
  if (metric === "power") {
    // Derived, never archived: P = U x I. Both factors must be real readings —
    // a bucket that reported no voltage has no power, it does not have 0 kW.
    // Service frames and unreported zeros are already excluded upstream (the
    // lake's own measurement rules), so nothing here re-admits them.
    const volts = bucketValue(point.voltage);
    const amps = bucketValue(point.current);
    if (volts === null || amps === null) return null;
    return (volts * amps) / 1000;
  }
  if (metric === "session_energy") {
    const wh = highWaterValue(point.session_energy);
    return wh === null ? null : wh / WH_PER_KWH;
  }
  return bucketValue(point[metric]);
}

function bucketValue(bucket: SeriesMetricPoint | null | undefined): number | null {
  return bucket ? num(bucket.avg) : null;
}

function highWaterValue(bucket: SeriesMetricPoint | null | undefined): number | null {
  if (!bucket) return null;
  // `max` is the counter's value at the end of the bucket; `avg` is only a
  // fallback for a backend that sent no max at all.
  return num(bucket.max) ?? num(bucket.avg);
}

/**
 * One chart per metric, not one chart with six metrics.
 *
 * 240 V, 24 A and 38 °C on a shared axis is a chart where two of the three
 * lines are flat. Small multiples keep every metric readable, and every chart
 * carries one line: the value.
 */
export function SeriesCharts({
  series,
  metrics,
  bucketSeconds,
}: {
  series: SeriesPoint[];
  metrics: TelemetryChartMetric[];
  bucketSeconds?: number;
}) {
  if (!metrics.length) {
    return <div className="text-sm text-muted-foreground">No metric selected.</div>;
  }
  if (!series.length) {
    return <div className="text-sm text-muted-foreground">No samples to chart in this window.</div>;
  }

  return (
    <div className="space-y-3">
      {bucketSeconds ? (
        <div className="text-[11px] text-muted-foreground">
          Downsampled server-side to {formatSeconds(bucketSeconds)} buckets. Power is voltage ×
          current from the same buckets; a break in a line is a bucket that measured nothing.
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {metrics.map((metric) => (
          <MetricChart key={metric} metric={metric} series={series} />
        ))}
      </div>
    </div>
  );
}

function MetricChart({
  metric,
  series,
}: {
  metric: TelemetryChartMetric;
  series: SeriesPoint[];
}) {
  // Every bucket in the window is a point, including the ones that measured
  // nothing — dropping those would close the gap and draw a line the charger
  // never reported, and turning them into 0 would invent a dropout.
  const points = series
    .map((point) => {
      const ts = new Date(point.ts).getTime();
      return isFinite(ts)
        ? ([ts, metricValue(point, metric)] as [number, number | null])
        : null;
    })
    .filter((p): p is [number, number | null] => p !== null);

  const hasValue = points.some(([, value]) => value !== null);
  if (!hasValue) {
    return (
      <div className="rounded-lg border border-border p-3" data-testid={`telemetry-chart-${metric}`}>
        <div className="text-xs font-medium">{METRIC_LABEL[metric]}</div>
        <div className="mt-6 text-center text-xs text-muted-foreground">
          No archived values for this metric.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border p-2" data-testid={`telemetry-chart-${metric}`}>
      <div className="px-1 text-xs font-medium">{METRIC_LABEL[metric]}</div>
      <ApexChart
        series={[{ name: METRIC_LABEL[metric], data: points }]}
        type="line"
        height={180}
        colors={[METRIC_COLOR[metric]]}
      />
    </div>
  );
}
